import { Typography } from "@mui/material";
import { useState } from "react";
import styled from "styled-components";
import { CellGrid } from "../../../../components/common/cell-grid/CellGrid";
import { AddedDefectEntry } from "../../../../types/Inspection";
import { Defect } from "../defects";
import { ModuleSize } from "../../../../types/ModuleSize";

const TopPanels = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
`;

const DefectsPanel = styled.div`
  flex: 1;
  border: 1px solid #ccc;
  padding: 16px;
  border-radius: 8px;
  height: 500px;
  overflow-y: auto;
`;

const CellGridPanel = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DefectCategory = styled.div`
  margin-bottom: 16px;
  h4 {
    margin: 0 0 8px 0;
    border-bottom: 1px solid #eee;
    padding-bottom: 4px;
  }
`;

const DefectItem = styled.label<{ selected: boolean }>`
  display: block;
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? "#ffe8cc" : "transparent")};
  &:hover {
    background-color: ${(props) => (props.selected ? "#ffe8cc" : "#f0f0f0")};
  }
  input {
    margin-right: 8px;
  }
`;

const AddButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: bold;
  color: white;
  background-color: #27ae60;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 24px;
  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
  }
`;

const DefectsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  th,
  td {
    border: 1px solid #ccc;
    padding: 8px;
    text-align: left;
  }
  th {
    background-color: #f2f2f2;
  }
  .actions {
    width: 80px;
    text-align: center;
  }
`;

const DeleteButton = styled.button`
  padding: 4px 8px;
  color: white;
  background-color: #c0392b;
  border: none;
  border-radius: 4px;
  cursor: pointer;
`;

function Defects({
  addedDefects,
  setAddedDefects,
  defectsData,
  size,
  bordered,
}: {
  addedDefects: AddedDefectEntry[];
  setAddedDefects: React.Dispatch<React.SetStateAction<AddedDefectEntry[]>>;
  defectsData: Record<string, Defect[]>;
  size: ModuleSize;
  bordered: boolean;
}) {
  const [currentSelectedCells, setCurrentSelectedCells] = useState<string[]>(
    []
  );
  const [currentSelectedDefects, setCurrentSelectedDefects] = useState<
    Defect[]
  >([]);
  const handleDefectToggle = (defect: Defect, isSelected: boolean) => {
    if (isSelected) {
      setCurrentSelectedDefects((prev) => [...prev, defect]);
    } else {
      setCurrentSelectedDefects((prev) =>
        prev.filter((d) => d.id !== defect.id)
      );
    }
  };

  const handleAddEntry = () => {
    if (
      currentSelectedCells.length === 0 ||
      currentSelectedDefects.length === 0
    )
      return;

    const newEntry: AddedDefectEntry = {
      id: Date.now(),
      cells: currentSelectedCells,
      defects: currentSelectedDefects,
    };

    setAddedDefects((prev) => [...prev, newEntry]);

    setCurrentSelectedCells([]);
    setCurrentSelectedDefects([]);
  };

  const handleDeleteEntry = (id: number) => {
    setAddedDefects((prev) => prev.filter((entry) => entry.id !== id));
  };

  return (
    <>
      <TopPanels>
        <DefectsPanel>
          <Typography variant="h5" fontWeight="bold" mb={3}>
            Select Defects
          </Typography>
          {Object.entries(defectsData).map(([category, defects]) => (
            <DefectCategory key={category}>
              <h4>{category}</h4>
              {defects.map((defect) => (
                <DefectItem
                  key={defect.id}
                  selected={currentSelectedDefects.some(
                    (d) => d.id === defect.id
                  )}
                >
                  <input
                    type="checkbox"
                    checked={currentSelectedDefects.some(
                      (d) => d.id === defect.id
                    )}
                    onChange={(e) =>
                      handleDefectToggle(defect, e.target.checked)
                    }
                  />
                  {defect.id} - {defect.description}
                </DefectItem>
              ))}
            </DefectCategory>
          ))}
        </DefectsPanel>

        <CellGridPanel>
          <CellGrid
            size={size}
            bordered={bordered}
            onSelectionChange={setCurrentSelectedCells}
            selected={currentSelectedCells}
          />
        </CellGridPanel>
      </TopPanels>

      <AddButton
        onClick={handleAddEntry}
        disabled={
          currentSelectedCells.length === 0 ||
          currentSelectedDefects.length === 0
        }
      >
        Add Selected Cells & Defects to Report
      </AddButton>

      {addedDefects.length > 0 && (
        <DefectsTable>
          <thead>
            <tr>
              <th>Cells</th>
              <th>Defects</th>
              <th className="actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {addedDefects.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.cells.join(", ")}</td>
                <td>
                  {entry.defects
                    .map((d) => `${d.id} - ${d.description}`)
                    .join(" | ")}
                </td>
                <td className="actions">
                  <DeleteButton onClick={() => handleDeleteEntry(entry.id)}>
                    Delete
                  </DeleteButton>
                </td>
              </tr>
            ))}
          </tbody>
        </DefectsTable>
      )}
    </>
  );
}

export default Defects;
